import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { MultiSelect } from 'primereact/multiselect';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import dayjs from 'dayjs';
import { lagerCompaniesAPI, lagerFinishedProductsAPI, lagerLocationsAPI, lagerProductionAPI, lagerTransportAPI } from '../services/api';
import { LagerCompany, LagerFinishedProduct, LagerLocation, LagerPlacement, LagerProductionRecord, LagerShift, LagerTransport } from '../types';
import { showCustomAlert } from './CustomAlert';
import { escapeHtml, openPrintWindow } from '../utils/printableReport';

type Filters = {
  range: [Date, Date | null];
  productIds: string[];
  locationIds: string[];
  shifts: LagerShift[];
  companyIds: string[];
};

const todayRange = (): [Date, Date] => [dayjs().startOf('day').toDate(), dayjs().startOf('day').toDate()];

const defaultFilters = (): Filters => ({ range: todayRange(), productIds: [], locationIds: [], shifts: [], companyIds: [] });

const shiftOptions = [
  { label: 'I smena', value: 'I' },
  { label: 'II smena', value: 'II' },
  { label: 'III smena', value: 'III' }
];

const formatDateSr = (d: Date) => d.toLocaleDateString('sr-RS');
const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

const transportProductId = (t: LagerTransport) =>
  t.originalProductId || (typeof t.productId === 'string' ? t.productId : t.productId?._id) || '';
const transportProductName = (t: LagerTransport) =>
  (typeof t.productId !== 'string' ? t.productId?.name : '') || t.productName || '';
const transportCompanyId = (t: LagerTransport) => (typeof t.companyId === 'string' ? t.companyId : t.companyId?._id) || '';
const transportCompanyName = (t: LagerTransport) =>
  (typeof t.companyId !== 'string' ? t.companyId?.name : '') || t.companyName || '';

const computeTransportM2 = (t: LagerTransport, productMap: Map<string, LagerFinishedProduct>): number => {
  const qty = Number(t.quantity || 0);
  const unit = t.unit;

  // Prefer explicit squareMeters if present
  const m2 = Number(t.squareMeters ?? 0);
  if (!m2 && unit === 'm2') return qty;

  const product = productMap.get(transportProductId(t));
  if (!product) return m2 || 0;

  const m2PerPiece = Number(product.m2PerPiece || 0);
  const perPallet = Number(product.perPallet || 0);

  if (unit === 'kom' && m2PerPiece) return qty * m2PerPiece;
  if (unit === 'paleta' && m2PerPiece && perPallet) return qty * perPallet * m2PerPiece;

  return m2 || 0;
};

// Palete: unete u paletama se prikazuju tačno, ostalo se preračunava iz m² po definiciji proizvoda
const palletsOf = (quantity: number, unit: string, m2: number, product?: LagerFinishedProduct) => {
  if (unit === 'paleta') return Number(quantity || 0);
  const m2PerPallet = Number(product?.m2PerPiece || 0) * Number(product?.perPallet || 0);
  return m2PerPallet ? m2 / m2PerPallet : 0;
};

const quantityText = (quantity: number, unit: string) => (unit === 'paleta' ? '' : `${fmt(Number(quantity || 0))} ${unit === 'm2' ? 'm²' : unit}`);

const placementText = (p: LagerPlacement) => `${p.locationName || ''} · ${p.rowName || ''} · ${p.pallets}`;

const shiftLabel = (s: LagerShift) => (s === 'I' ? 'I smena' : s === 'II' ? 'II smena' : 'III smena');

const LagerReports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [activeTab, setActiveTab] = useState(0);

  const [products, setProducts] = useState<LagerFinishedProduct[]>([]);
  const [locations, setLocations] = useState<LagerLocation[]>([]);
  const [companies, setCompanies] = useState<LagerCompany[]>([]);
  const [production, setProduction] = useState<LagerProductionRecord[]>([]);
  const [transports, setTransports] = useState<LagerTransport[]>([]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [prods, locs, comps, prod, trans] = await Promise.all([
        lagerFinishedProductsAPI.getAll(),
        lagerLocationsAPI.getAll(),
        lagerCompaniesAPI.getAll(),
        lagerProductionAPI.getAll({ showAll: true }),
        lagerTransportAPI.getAll()
      ]);
      setProducts(Array.isArray(prods) ? prods : []);
      setLocations(Array.isArray(locs) ? locs : []);
      setCompanies(Array.isArray(comps) ? comps : []);
      setProduction(Array.isArray(prod) ? prod : []);
      setTransports(Array.isArray(trans) ? trans : []);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Greška pri učitavanju podataka za izveštaj.';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const productMap = useMemo(() => new Map(products.map((p) => [p._id, p])), [products]);

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => setFilters((f) => ({ ...f, [key]: value }));

  const [from, to] = filters.range;
  const isTodayOnly = dayjs(from).isSame(dayjs(), 'day') && (!to || dayjs(to).isSame(dayjs(), 'day'));
  const periodStr = !to || dayjs(from).isSame(to, 'day') ? formatDateSr(from) : `${formatDateSr(from)} - ${formatDateSr(to)}`;
  const hasFilters =
    !isTodayOnly || filters.productIds.length > 0 || filters.locationIds.length > 0 || filters.shifts.length > 0 || filters.companyIds.length > 0;

  // Redovi tabela sa već izračunatim paletama i m²
  const prodRows = useMemo(() => {
    const fromTs = dayjs(from).startOf('day').valueOf();
    const toTs = dayjs(to || from).endOf('day').valueOf();
    return production
      .filter((r) => {
        const ts = dayjs(r.producedDate).valueOf();
        if (ts < fromTs || ts > toTs) return false;
        if (filters.productIds.length && !filters.productIds.includes(String(r.productId))) return false;
        if (filters.locationIds.length && !(r.placements || []).some((p) => filters.locationIds.includes(String(p.locationId)))) return false;
        if (filters.shifts.length && !filters.shifts.includes(r.shift)) return false;
        return true;
      })
      .map((r) => {
        const m2 = Number(r.m2Total || 0);
        return { ...r, m2, palete: palletsOf(r.quantity, r.unit, m2, productMap.get(String(r.productId))), date: dayjs(r.producedDate).valueOf() };
      })
      .sort((a, b) => b.date - a.date);
  }, [production, filters, from, to, productMap]);

  const transRows = useMemo(() => {
    const fromTs = dayjs(from).startOf('day').valueOf();
    const toTs = dayjs(to || from).endOf('day').valueOf();
    return transports
      .filter((t) => {
        const ts = dayjs(t.shippedDate).valueOf();
        if (ts < fromTs || ts > toTs) return false;
        if (filters.productIds.length && !filters.productIds.includes(transportProductId(t))) return false;
        if (filters.locationIds.length && !(t.placements || []).some((p) => filters.locationIds.includes(String(p.locationId)))) return false;
        if (filters.companyIds.length && !filters.companyIds.includes(transportCompanyId(t))) return false;
        return true;
      })
      .map((t) => {
        const m2 = computeTransportM2(t, productMap);
        return {
          ...t,
          m2,
          palete: palletsOf(t.quantity, t.unit, m2, productMap.get(transportProductId(t))),
          product: transportProductName(t),
          company: transportCompanyName(t),
          date: dayjs(t.shippedDate).valueOf()
        };
      })
      .sort((a, b) => b.date - a.date);
  }, [transports, filters, from, to, productMap]);

  type ProdRow = (typeof prodRows)[number];
  type TransRow = (typeof transRows)[number];

  const filterParts = (forProduction: boolean) => {
    const names = <T extends { _id: string; name: string }>(list: T[], ids: string[]) =>
      ids.map((id) => list.find((x) => x._id === id)?.name || '').filter(Boolean).join(', ');
    const parts = [`Period: ${periodStr}`];
    if (filters.productIds.length) parts.push(`Proizvodi: ${names(products, filters.productIds)}`);
    if (filters.locationIds.length) parts.push(`Lokacije: ${names(locations, filters.locationIds)}`);
    if (forProduction && filters.shifts.length) parts.push(`Smene: ${filters.shifts.map(shiftLabel).join(', ')}`);
    if (!forProduction && filters.companyIds.length) parts.push(`Firme: ${names(companies, filters.companyIds)}`);
    return parts;
  };

  // Štampa se kartica koja je otvorena
  const handlePrint = () => {
    const isProd = activeTab === 0;
    const rows = isProd ? prodRows : transRows;
    if (!rows.length) {
      showCustomAlert.warning('Nema zapisa za izabrane filtere.');
      return;
    }
    const title = isProd ? 'Izveštaj proizvodnje' : 'Izveštaj transporta';
    const placementsHtml = (list?: LagerPlacement[]) => (list || []).map((p) => escapeHtml(placementText(p))).join('<br/>');

    let body = `
      <h1>${title} - Lager gotovih proizvoda</h1>
      <div class="meta">
        ${filterParts(isProd)
          .map((p) => `<div>${escapeHtml(p)}</div>`)
          .join('')}
        <div class="muted">Generisano: ${escapeHtml(new Date().toLocaleString('sr-RS'))}</div>
      </div>
    `;

    if (isProd) {
      body += `<table>
        <thead><tr><th>Datum</th><th>Proizvod</th><th class="right">Palete</th><th>Količina</th><th class="right">m²</th><th>Lokacija · Red · Palete</th><th>Smena</th></tr></thead>
        <tbody>
          ${prodRows
            .map(
              (r) => `<tr>
                <td>${escapeHtml(formatDateSr(new Date(r.producedDate)))}</td>
                <td>${escapeHtml(r.productName)}</td>
                <td class="right">${escapeHtml(fmt(r.palete))}</td>
                <td>${escapeHtml(quantityText(r.quantity, r.unit))}</td>
                <td class="right">${escapeHtml(r.m2.toFixed(2))}</td>
                <td>${placementsHtml(r.placements)}</td>
                <td>${escapeHtml(shiftLabel(r.shift))}</td>
              </tr>`
            )
            .join('')}
        </tbody>
      </table>`;
    } else {
      body += `<table>
        <thead><tr><th>Datum</th><th>Proizvod</th><th>Firma</th><th class="right">Palete</th><th>Količina</th><th class="right">m²</th><th>Utovareno iz</th><th>Napomena</th></tr></thead>
        <tbody>
          ${transRows
            .map(
              (t) => `<tr>
                <td>${escapeHtml(formatDateSr(new Date(t.shippedDate)))}</td>
                <td>${escapeHtml(t.product)}</td>
                <td>${escapeHtml(t.company)}</td>
                <td class="right">${escapeHtml(fmt(t.palete))}</td>
                <td>${escapeHtml(quantityText(t.quantity, t.unit))}</td>
                <td class="right">${escapeHtml(t.m2.toFixed(2))}</td>
                <td>${placementsHtml(t.placements)}</td>
                <td>${escapeHtml(t.notes || '')}</td>
              </tr>`
            )
            .join('')}
        </tbody>
      </table>`;
    }

    const ok = openPrintWindow(title, body);
    if (!ok) showCustomAlert.error('Pregled izveštaja nije mogao da se otvori u novom prozoru.');
  };

  const placementTags = (list?: LagerPlacement[]) => (
    <div className="flex flex-wrap gap-1">
      {(list || []).map((p, i) => (
        <Tag key={i} value={placementText(p)} style={{ background: '#eef2f7', color: '#334155', fontWeight: 500, border: '1px solid #dbe3ec' }} />
      ))}
      {!(list || []).length && <span style={{ color: '#94a3b8' }}>—</span>}
    </div>
  );

  const palletsBody = (palete: number) => <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{fmt(palete)}</span>;
  const mutedBody = (text: string) => <span style={{ color: '#64748b' }}>{text}</span>;
  const dateBody = (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{formatDateSr(new Date(value))}</span>;

  const filterField = (width: number, el: React.ReactNode) => <div style={{ flex: `1 1 ${width}px`, minWidth: Math.min(width, 200) }}>{el}</div>;

  const tableProps = {
    loading,
    paginator: true,
    rows: 25,
    rowsPerPageOptions: [25, 50, 100],
    responsiveLayout: 'stack' as const,
    breakpoint: '768px',
    dataKey: '_id',
    size: 'small' as const,
    stripedRows: true
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Izveštaji">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="flex flex-wrap align-items-center gap-2" style={{ marginBottom: '0.5rem' }}>
          {filterField(
            230,
            <Calendar
              value={filters.range}
              onChange={(e) => {
                const v = e.value as (Date | null)[] | null;
                if (v && v[0]) setFilter('range', [v[0], v[1] || null]);
              }}
              selectionMode="range"
              readOnlyInput
              dateFormat="dd.mm.yy"
              showIcon
              className="w-full"
            />
          )}
          {filterField(
            200,
            <MultiSelect
              value={filters.productIds}
              options={products}
              optionLabel="name"
              optionValue="_id"
              placeholder="Svi proizvodi"
              maxSelectedLabels={1}
              selectedItemsLabel="{0} proizvoda"
              filter
              className="w-full"
              onChange={(e) => setFilter('productIds', e.value || [])}
            />
          )}
          {filterField(
            180,
            <MultiSelect
              value={filters.locationIds}
              options={locations}
              optionLabel="name"
              optionValue="_id"
              placeholder="Sve lokacije"
              maxSelectedLabels={1}
              selectedItemsLabel="{0} lokacije"
              filter
              className="w-full"
              onChange={(e) => setFilter('locationIds', e.value || [])}
            />
          )}
          {activeTab === 0
            ? filterField(
                150,
                <MultiSelect
                  value={filters.shifts}
                  options={shiftOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Sve smene"
                  maxSelectedLabels={1}
                  selectedItemsLabel="{0} smene"
                  className="w-full"
                  onChange={(e) => setFilter('shifts', e.value || [])}
                />
              )
            : filterField(
                180,
                <MultiSelect
                  value={filters.companyIds}
                  options={companies}
                  optionLabel="name"
                  optionValue="_id"
                  placeholder="Sve firme"
                  maxSelectedLabels={1}
                  selectedItemsLabel="{0} firme"
                  filter
                  className="w-full"
                  onChange={(e) => setFilter('companyIds', e.value || [])}
                />
              )}
          {hasFilters && <Button icon="pi pi-filter-slash" text tooltip="Poništi filtere (danas)" onClick={() => setFilters(defaultFilters())} />}
          <Button icon="pi pi-refresh" text tooltip="Osveži" onClick={load} loading={loading} />
          <Button label="Štampaj / PDF" icon="pi pi-print" outlined onClick={handlePrint} />
        </div>

        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          <TabPanel header={`Proizvodnja (${prodRows.length})`} leftIcon="pi pi-arrow-down mr-2">
            <DataTable value={prodRows} emptyMessage={`Nema proizvodnje za ${isTodayOnly ? 'danas' : 'izabrani period'}`} {...tableProps}>
              <Column header="Datum" body={(r: ProdRow) => dateBody(r.producedDate)} sortable sortField="date" style={{ width: '110px' }} />
              <Column field="productName" header="Proizvod" sortable />
              <Column header="Palete" body={(r: ProdRow) => palletsBody(r.palete)} sortable sortField="palete" style={{ width: '90px' }} />
              <Column header="Lokacija · Red · Palete" body={(r: ProdRow) => placementTags(r.placements)} />
              <Column header="Smena" body={(r: ProdRow) => shiftLabel(r.shift)} sortable sortField="shift" style={{ width: '100px' }} />
              <Column
                header="Količina / m²"
                body={(r: ProdRow) => mutedBody([quantityText(r.quantity, r.unit), `${r.m2.toFixed(2)} m²`].filter(Boolean).join(' · '))}
                style={{ width: '200px', whiteSpace: 'nowrap' }}
              />
            </DataTable>
          </TabPanel>

          <TabPanel header={`Transport (${transRows.length})`} leftIcon="pi pi-arrow-up mr-2">
            <DataTable value={transRows} emptyMessage={`Nema transporta za ${isTodayOnly ? 'danas' : 'izabrani period'}`} {...tableProps}>
              <Column header="Datum" body={(t: TransRow) => dateBody(t.shippedDate)} sortable sortField="date" style={{ width: '110px' }} />
              <Column field="product" header="Proizvod" sortable />
              <Column field="company" header="Firma" sortable />
              <Column header="Palete" body={(t: TransRow) => palletsBody(t.palete)} sortable sortField="palete" style={{ width: '90px' }} />
              <Column header="Utovareno iz" body={(t: TransRow) => placementTags(t.placements)} />
              <Column
                header="Količina / m²"
                body={(t: TransRow) => mutedBody([quantityText(t.quantity, t.unit), `${t.m2.toFixed(2)} m²`].filter(Boolean).join(' · '))}
                style={{ width: '200px', whiteSpace: 'nowrap' }}
              />
              <Column header="Napomena" body={(t: TransRow) => mutedBody(t.notes || '')} />
            </DataTable>
          </TabPanel>
        </TabView>
      </Card>
    </div>
  );
};

export default LagerReports;
